// src/pages/attachment/AttachmentManagerPage.tsx
// Attachment Manager — home for the attachment module. Replaces the
// generated CRUD scaffold (AttachmentListPage/DetailPage/EditPage/
// SettingsPage), which modeled "an attachment" as a table row and wired
// 8 of 19 RPCs. See docs/ux/attachment-ia.md for the full placement.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { Button, Badge } from '@react/components';
import { attachmentApi } from '@core/api';
import type { Attachment } from '@core/types/attachment';
import { uploadAttachment } from '@/features/chat/logic';
import { uploadAttachmentMultipart } from '@/features/attachment/upload';
import {
  MIME_PREFIX_FILTERS,
  MULTIPART_THRESHOLD_BYTES,
  attachmentId,
  deleteAttachmentsBulk,
  formatFileSize,
  getFileExtension,
  isImageMimeType,
  processingStatusVariant,
} from '@/features/attachment/logic';
import { errMessage } from '@/features/attachment/format';
import { AttachmentDetailRail } from '@/features/attachment/AttachmentDetailRail';
import { LookupByIdsDialog } from '@/features/attachment/LookupByIdsDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';

interface UploadJob {
  id: string;
  name: string;
  progress: number;
  error?: string;
}

const PAGE_LIMIT = 40;

export const AttachmentManagerPage = () => {
  const { t } = useI18n();
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [items, setItems] = useState<Attachment[]>([]);
  const [cursor, setCursor] = useState('');
  const [hasNext, setHasNext] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [mimePrefix, setMimePrefix] = useState('');
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [nameFilter, setNameFilter] = useState('');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [railId, setRailId] = useState<string | null>(routeId ?? null);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const [uploads, setUploads] = useState<UploadJob[]>([]);

  const load = useCallback((reset: boolean) => {
    const setLoading = reset ? setIsLoading : setIsLoadingMore;
    setLoading(true);
    setError(null);
    const params: any = {
      pagination: { cursor: reset ? '' : cursor, limit: PAGE_LIMIT, direction: 'forward' },
      sort: [],
      view: 0,
      include_download_urls: true,
    };
    if (mimePrefix) params.mime_type_prefix = mimePrefix;
    if (onlyFailed) params.processing_status = 'ATTACHMENT_PROCESSING_STATUS_FAILED';

    attachmentApi.ListAttachments(params)
      .then((res) => {
        const next = res.attachments?.attachments ?? [];
        setItems((prev) => (reset ? next : [...prev, ...next]));
        setCursor(res.pagination?.page_info?.next_cursor ?? '');
        setHasNext(res.pagination?.page_info?.has_next ?? false);
      })
      .catch((e) => setError(errMessage(e, t('attachment.manager.loadError'))))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mimePrefix, onlyFailed, cursor, t]);

  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mimePrefix, onlyFailed]);

  const visible = nameFilter.trim()
    ? items.filter((a) => a.file_name.toLowerCase().includes(nameFilter.trim().toLowerCase()))
    : items;

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleUploadFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      const jobId = `${file.name}-${Date.now()}-${Math.random()}`;
      setUploads((prev) => [...prev, { id: jobId, name: file.name, progress: 0 }]);
      const onProgress = (progress: number) => {
        setUploads((prev) => prev.map((u) => (u.id === jobId ? { ...u, progress } : u)));
      };
      try {
        const result = file.size > MULTIPART_THRESHOLD_BYTES
          ? await uploadAttachmentMultipart(file, onProgress)
          : await uploadAttachment(file, onProgress);
        const res = await attachmentApi.GetAttachment(result.entityId, { params: { include_download_url: true } });
        if (res.attachment) {
          setItems((prev) => [res.attachment as Attachment, ...prev]);
        }
        setUploads((prev) => prev.filter((u) => u.id !== jobId));
      } catch (e) {
        setUploads((prev) => prev.map((u) => (u.id === jobId ? { ...u, error: errMessage(e, t('attachment.manager.upload.error')) } : u)));
      }
    }
  };

  const handleBulkDownload = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      const res = await attachmentApi.GetDownloadUrlsBulk({ attachment_ids: ids, inline: false });
      Object.values(res.urls ?? {}).forEach((u) => {
        if (u.url) window.open(u.url, '_blank', 'noopener,noreferrer');
      });
      if ((res.failed_ids ?? []).length > 0) {
        setError(t('attachment.manager.bulk.downloadPartial', { count: res.failed_ids.length }));
      }
    } catch (e) {
      setError(errMessage(e, t('attachment.manager.bulk.downloadError')));
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setIsBulkDeleting(true);
    try {
      const res = await deleteAttachmentsBulk(ids, false);
      const deletedSet = new Set(res.deleted_ids);
      setItems((prev) => prev.filter((a) => !deletedSet.has(attachmentId(a))));
      setSelected(new Set());
      if ((res.failed_ids ?? []).length > 0) {
        setError(t('attachment.manager.bulk.deletePartial', { count: res.failed_ids.length }));
      }
    } catch (e) {
      setError(errMessage(e, t('attachment.manager.bulk.deleteError')));
    } finally {
      setIsBulkDeleting(false);
      setConfirmBulkDelete(false);
    }
  };

  const handleOpenRail = (id: string) => {
    setRailId(id);
    navigate(`/attachments/${id}`, { replace: false });
  };

  const handleCloseRail = () => {
    setRailId(null);
    navigate('/attachments', { replace: false });
  };

  return (
    <div className="flex h-full">
      <div className="flex-1 min-w-0 space-y-4 p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {/* The tab already names this page; the heading stays for screen readers. */}
          <h1 className="sr-only">{t('attachment.manager.title')}</h1>
          <div className="flex items-center gap-2 sm:ml-auto">
            <Button variant="secondary" size="sm" onClick={() => setLookupOpen(true)}>
              {t('attachment.manager.lookup.button')}
            </Button>
            <Button size="sm" onClick={() => fileInputRef.current?.click()}>
              {t('attachment.manager.upload.button')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => { handleUploadFiles(e.target.files); e.target.value = ''; }}
            />
          </div>
        </div>

        {uploads.length > 0 && (
          <div className="space-y-1.5 rounded-md border border-border p-3">
            {uploads.map((u) => (
              <div key={u.id} className="flex items-center gap-2 text-sm">
                <span className="flex-1 truncate text-foreground">{u.name}</span>
                {u.error ? (
                  <span className="text-destructive">{u.error}</span>
                ) : (
                  <span className="text-muted-foreground">{u.progress}%</span>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {MIME_PREFIX_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setMimePrefix(f.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                mimePrefix === f.value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'
              }`}
            >
              {t(f.labelKey)}
            </button>
          ))}
          <button
            onClick={() => setOnlyFailed((v) => !v)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              onlyFailed ? 'bg-destructive text-destructive-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'
            }`}
          >
            {t('attachment.manager.filter.failed')}
          </button>
          <input
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            placeholder={t('common.search')}
            className="ml-auto w-48 rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
            {t('common.loading')}
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm text-muted-foreground">{t('attachment.manager.empty')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {visible.map((a) => {
              const id = attachmentId(a);
              const isImage = isImageMimeType(a.mime_type);
              const isChecked = selected.has(id);
              return (
                <div
                  key={id}
                  role="button"
                  tabIndex={0}
                  aria-label={a.file_name}
                  className={`group relative flex flex-col overflow-hidden rounded-lg border transition-colors cursor-pointer ${
                    isChecked ? 'border-primary ring-1 ring-primary' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => handleOpenRail(id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOpenRail(id); } }}
                >
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleSelect(id); }}
                    className={`absolute left-2 top-2 z-10 flex h-5 w-5 items-center justify-center rounded border bg-background ${
                      isChecked ? 'border-primary bg-primary text-primary-foreground' : 'border-border opacity-0 group-hover:opacity-100'
                    }`}
                    aria-label={t('attachment.manager.select')}
                  >
                    {isChecked && (
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                    )}
                  </button>
                  <div className="flex h-28 items-center justify-center bg-muted">
                    {isImage && a.download_url?.url ? (
                      <img src={a.download_url.url} alt={a.file_name} className="h-full w-full object-cover" loading="lazy" />
                    ) : (
                      <span className="text-sm font-bold text-muted-foreground">{getFileExtension(a.file_name) || 'FILE'}</span>
                    )}
                  </div>
                  <div className="space-y-1 p-2">
                    <p className="truncate text-xs font-medium text-foreground">{a.file_name}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-muted-foreground">{formatFileSize(a.size_bytes)}</span>
                      {a.processing_status && a.processing_status !== 'ATTACHMENT_PROCESSING_STATUS_COMPLETE' && (
                        <Badge variant={processingStatusVariant(a.processing_status)} size="sm">
                          {t(`attachment.manager.status.${a.processing_status}`)}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {hasNext && !isLoading && (
          <div className="flex justify-center pt-2">
            <Button variant="secondary" size="sm" onClick={() => load(false)} isLoading={isLoadingMore}>
              {t('common.loadMore')}
            </Button>
          </div>
        )}
      </div>

      {railId && (
        <AttachmentDetailRail
          attachmentId={railId}
          onClose={handleCloseRail}
          onDeleted={(id) => setItems((prev) => prev.filter((a) => attachmentId(a) !== id))}
          onUpdated={(updated) => setItems((prev) => prev.map((a) => (attachmentId(a) === attachmentId(updated) ? updated : a)))}
        />
      )}

      {selected.size > 0 && (
        <div role="toolbar" aria-label={t('attachment.manager.bulk.toolbar')} className="fixed bottom-6 left-1/2 z-30 -translate-x-1/2">
          <div className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground shadow-lg">
            <span className="text-sm font-medium">{t('attachment.manager.bulk.selected', { count: selected.size })}</span>
            <div className="h-4 w-px bg-primary-foreground/30" />
            <button onClick={handleBulkDownload} className="rounded-md px-2 py-1 text-sm hover:bg-primary-foreground/10">
              {t('attachment.manager.rail.download')}
            </button>
            <button onClick={() => setConfirmBulkDelete(true)} className="rounded-md px-2 py-1 text-sm hover:bg-destructive/80">
              {t('common.delete')}
            </button>
            <button onClick={() => setSelected(new Set())} className="rounded-md px-2 py-1 text-sm hover:bg-primary-foreground/10">
              {t('workspace.bulk.clearSelection')}
            </button>
          </div>
        </div>
      )}

      <LookupByIdsDialog
        open={lookupOpen}
        onClose={() => setLookupOpen(false)}
        onFound={(found) => {
          setItems((prev) => {
            const ids = new Set(prev.map(attachmentId));
            const merged = [...found.filter((a) => !ids.has(attachmentId(a))), ...prev];
            return merged;
          });
          setSelected((prev) => {
            const next = new Set(prev);
            found.forEach((a) => next.add(attachmentId(a)));
            return next;
          });
        }}
      />

      <ConfirmDialog
        open={confirmBulkDelete}
        title={t('attachment.manager.bulk.deleteConfirm', { count: selected.size })}
        destructive
        isConfirming={isBulkDeleting}
        onConfirm={handleBulkDelete}
        onCancel={() => setConfirmBulkDelete(false)}
      />
    </div>
  );
};

export default AttachmentManagerPage;
